import { MapColorThemeContextProps, MapStyle } from "@app/shared/map-appearance/interfaces";
import { MapColorThemeEditFlyout } from "@app/shared/map-appearance/components/MapColorThemeEditFlyout";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalContent } from "@app/shared/Modals";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { Context, FC, useContext, useState } from "react";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";

interface MapAppearancePanelProps {
  /** The context defining color themes returned by `createMapColorThemeContext()`. */
  colorThemeContext: Context<MapColorThemeContextProps | null>;
  /** Title of the flyout used to edit color themes. */
  colorThemeFlyoutTitle?: string;
  /** Label for color theme select element. */
  colorThemeSelectLabel?: string;
  /** Called when user selects a different style in `styleOptions`. */
  onStyleChange?: (newStyleUrl: string) => void;
  /** The current selected style's url. Determines which radio button is checked. */
  selectedStyle?: string;
  /** The style options the user can choose from. Map style options are hidden if this is undefined or empty. */
  styleOptions?: MapStyle[];
  /** Prefix used for various data-testids. */
  testId: string;
}

const warningModal: Omit<ModalContent, "confirmButtonAction"> = {
  body: "You will lose unsaved changes. Continue with closing the panel?",
  cancelButtonText: "Cancel",
  confirmButtonText: "Continue",
  title: "Warning",
};

export const MapAppearancePanel: FC<MapAppearancePanelProps> = ({
  colorThemeContext,
  colorThemeFlyoutTitle = "Color Themes",
  colorThemeSelectLabel = "Color Theme",
  onStyleChange,
  selectedStyle,
  styleOptions,
  testId,
}) => {
  const ctx = useContext(colorThemeContext);
  if (!ctx) throw new Error("MapAppearancePanel must be used within a MapColorThemeContextProvider");

  const [isDirty, setIsDirty] = useState(false);

  return (
    <div className="map-appearance__selector-container">
      <QESSelect
        label={colorThemeSelectLabel}
        onChange={(e) => ctx.setTheme(e.target.value)}
        testId={`${testId}__map-appearance__color-theme-select`}
        value={ctx.currentTheme.uuid}
      >
        <optgroup label="Preset Themes">
          {ctx.presetThemes.map(theme => (
            <option key={theme.uuid} value={theme.uuid}>
              {theme.name}
            </option>
          ))}
        </optgroup>
        {ctx.customThemes.length > 0 && (
          <optgroup label="Custom Themes">
            {ctx.customThemes.map(theme => (
              <option key={theme.uuid} value={theme.uuid}>
                {theme.name}
              </option>
            ))}
          </optgroup>
        )}
      </QESSelect>

      <FlyoutLink
        classNames="qes-button-secondary map-appearance__edit-flyout-link"
        content={{
          body: (
            <MapColorThemeEditFlyout
              colorThemeContext={colorThemeContext}
              onDirtyChange={setIsDirty}
              warningModal={warningModal}
            />
          ),
          title: colorThemeFlyoutTitle,
        }}
        modalContent={warningModal}
        onClose={() => {
          ctx.setThemeToEdit(null);
          ctx.setEditingSlotIndex(null);
        }}
        size="40"
        testId={`${testId}__map-appearance__color-theme-edit`}
        warnOnClose={isDirty}
      >
        Edit Themes
      </FlyoutLink>

      {styleOptions && styleOptions.length > 0 && (
        <>
          <div className="map-appearance__map-styles__label">Map Style</div>
          <div className="map-appearance__map-styles__container">
            {styleOptions.map(({ name, url }) => (
              <QESRadioButton
                key={name}
                name={`${testId}__map-style`}
                checked={selectedStyle === url}
                onChange={(e) => onStyleChange?.(e.target.value)}
                label={name}
                testId={`${testId}__map-appearance__map-style-radio-btn--${name}`}
                value={url}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
